'use client'

import { sendGTMEvent } from '@next/third-parties/google'
import { QueryClient, useQueryClient } from '@tanstack/react-query'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { SubmitHandler } from 'react-hook-form'
import { useShallow } from 'zustand/react/shallow'

import { OtpLoginSuccessDetails } from 'lib/components/AccountPrompt/OtpLogin/OtpLogin'
// eslint-disable-next-line max-len
import { PasswordResetSuccessDetails } from 'lib/components/AccountPrompt/PasswordResetWithCode/PasswordResetWithCode'
import { AddUserModalOnAddProps } from 'lib/components/AddUserModal/AddUserModal'
import type { FormInput } from 'lib/components/AddUserModal/screens/NewUser'
import Button from 'lib/components/Button/Button'
import { COMPLETE_REGISTRATION_MODAL_ID } from 'lib/components/CompleteRegistrationModal/constants'
import type { HelpCenterItem } from 'lib/components/HelpCenter/HelpCenter'
import { MOBILE_VERIFY_MODAL_ID } from 'lib/components/MobileVerification/constants'
import { useModal } from 'lib/components/Modal/Modal'
import type { SignUpFormData } from 'lib/components/SignUpModal/SignUpModal'
import type { SiteFooterProps } from 'lib/components/SiteFooter/SiteFooter'
import SiteFooter from 'lib/components/SiteFooter/SiteFooter'
import { useModal as useMobileMenuModal } from 'lib/components/SiteHeader/MobileMenuModal'
import SiteHeader, { SiteHeaderProps } from 'lib/components/SiteHeader/SiteHeader'
import SmsOptInModal, { SMS_ONLY_MODAL_ID } from 'lib/components/SmsOptInModal/SmsOptInModal'

import useCreateUser, { UserCreatePayload } from '@/api/useCreateUserApi'
import useRecordSmsOptInApi from '@/api/useRecordSmsOptInApi'
import { useAutoCompleteProFacilitiesApi } from '@/app/book/api/useAutoCompleteProFacilitiesApi'
import { AppleTokenExchangeResponse } from '@/app/book/api/useExchangeAppleToken'
import { FacebookTokenExchangeResponse } from '@/app/book/api/useExchangeFacebookToken'
import { GoogleTokenExchangeResponse } from '@/app/book/api/useExchangeGoogleToken'
import { usePartialUserCreateApi } from '@/app/book/api/usePartialUserCreateApi'
import { useSearchUsersApi } from '@/app/book/api/useSearchUsersApi'
import useSessionsApi, { useLogOutSessionApi } from '@/app/book/api/useSessionsApi'
import { Traveler, useTravelersUpsertApi } from '@/app/book/api/useTravelerApi'
import { useAppleAuth } from '@/app/book/hooks/useAppleAuth'
import { useFacebookAuth } from '@/app/book/hooks/useFacebookAuth'
import { useGoogleAuth } from '@/app/book/hooks/useGoogleAuth'
import { routes } from '@/app/book/routes'
import useCartStore, { ShipmentNoteType } from '@/app/book/stores/useCartStore'
import { closeActiveBrazeInAppMessage, useBraze } from '@/hooks/useBraze'
import useHeader from '@/hooks/useHeader'
import { useMounted, usePageLoad } from '@/hooks/usePageLoad'
import { useRudderstackAnalytics } from '@/hooks/useRudderstackAnalytics'
import { isSmsMarketingEnabled, useSmsMarketingEnabled } from '@/hooks/useSmsMarketingEnabled'
import { useResolvedChatProvider } from '@/lib/chatProvider'
import { useDecagon } from '@/lib/decagon'
import { useZendesk } from '@/lib/zendesk'
import useIncompleteUserStore from '@/stores/useIncompleteUserStore'
import useMicroSiteStore from '@/stores/useMicroSiteStore'
import usePendingUserStore from '@/stores/usePendingUserStore'
import useSessionStore from '@/stores/useSessionStore'
import { redirectFromBookingWidget } from '@/utils/bookingWidgetRedirect'
import { brand } from '@/utils/brand'
import { buildSignUpSmsOptInVariables } from '@/utils/signupSmsOptIn'
import {
  isPromoModalSuppressedPath,
  openSmsOnlyModal,
  POST_CAPTURE_QUIET_MS,
  RETURN_VISIT_QUIET_MS,
} from '@/utils/smsOptInModalQueue'
import { trackGenerateLead } from '@/utils/trackGenerateLead'

import { useUserApi } from '../api/useUserApi'
import useAuthGate from '../hooks/useAuthGate'
import useCreateWelcomeLead from '../hooks/useCreateWelcomeLead'
import useDynamicGtm from '../hooks/useDynamicGtm'
import AirbrakeErrorBoundary from '../lib/airbrake'
import { setOptimizelyUser } from '../lib/optimizely'
import {
  isAdmin as checkIsAdmin,
  isAdminPlus as checkIsAdminPlus,
  isPro as checkIsPro,
  handleAdminProRedirect,
  hasAdminRole,
} from '../utils/adminProRedirect'

import { SITE_HEADER_BRANDS, SITE_HEADER_HELP_LINKS, SITE_HEADER_LINKS } from './siteHeaderConfig'

type LoginModalComponent = typeof import('lib/components/LoginModal/LoginModal').default
type SignUpModalComponent = typeof import('lib/components/SignUpModal/SignUpModal').default
type CompleteRegistrationModalComponent =
  typeof import('lib/components/CompleteRegistrationModal/CompleteRegistrationModal').default
type HelpCenterComponent = typeof import('lib/components/HelpCenter/HelpCenter').default
type MobileVerifyModalComponent =
  typeof import('lib/components/MobileVerification/MobileVerifyModal').default
type AddUserModalComponent = typeof import('lib/components/AddUserModal/AddUserModal').default
type ProRedirectModalComponent = typeof import('./ProRedirectModal').default

type Promisable<T> = T | PromiseLike<T>
type PersisterLike = { removeClient: () => Promisable<void> }
type PersisterRef = React.RefObject<PersisterLike | null>

type TokenExchangeResponse = FacebookTokenExchangeResponse | GoogleTokenExchangeResponse

interface ClientPortalProps {
  queryClient: QueryClient
  persisterRef: PersisterRef
}

const GTM_IDS = [process.env.NEXT_PUBLIC_GTM_ID, process.env.NEXT_PUBLIC_GTM_ID_PRO]
const BRAZE_BOOKING_WIDGET_BRAND = brand.key === 'shipskis' ? 'skis' : 'sticks'
const BRAZE_BOOKING_WIDGET_PLACEMENT =
  process.env.NEXT_PUBLIC_API_ENV === 'production'
    ? `booking-widget-banner-${BRAZE_BOOKING_WIDGET_BRAND}`
    : `booking-widget-banner-${BRAZE_BOOKING_WIDGET_BRAND}-dev`
const BRAZE_BANNER_PLACEMENTS: Record<string, string> = {
  '/booking-widget': BRAZE_BOOKING_WIDGET_PLACEMENT,
  '/': BRAZE_BOOKING_WIDGET_PLACEMENT,
}

// Enumerated carousel placement IDs (3 slots per location)
const BRAZE_CAROUSEL_SLOTS = [1, 2, 3] as const

const BRAZE_HOMEPAGE_CAROUSEL_IDS = BRAZE_CAROUSEL_SLOTS.map(
  (n) => `booking-widget-banner-${BRAZE_BOOKING_WIDGET_BRAND}-${n}`,
)

const BRAZE_NON_BOOK_CAROUSEL_IDS = BRAZE_CAROUSEL_SLOTS.map(
  (n) => `non-book-pages-${BRAZE_BOOKING_WIDGET_BRAND}-${n}`,
)

const BRAZE_ALL_PAGES_CAROUSEL_IDS = BRAZE_CAROUSEL_SLOTS.map(
  (n) => `all-pages-${BRAZE_BOOKING_WIDGET_BRAND}-${n}`,
)

/** Returns the carousel placement IDs for the given pathname. */
const getCarouselPlacementIds = (pathname: string): string[] => {
  if (pathname === '/' || pathname === '/booking-widget') return BRAZE_HOMEPAGE_CAROUSEL_IDS
  if (!excludeBannerOn(pathname)) return BRAZE_NON_BOOK_CAROUSEL_IDS
  return []
}

/** Returns the all-pages carousel IDs when appropriate for the pathname. */
const getAllPagesCarouselIds = (pathname: string): string[] => {
  if (excludeBannerOn(pathname)) return []
  return BRAZE_ALL_PAGES_CAROUSEL_IDS
}
const STATIC_HEADER_PATHS = new Set([
  '/claims',
  '/gift-cards',
  '/partner-agreement-terms',
  '/privacy',
  '/refer',
  '/reviews',
  '/terms',
])

const excludeBannerOn = (p: string): boolean => p.startsWith('/book') || p === '/order-confirmation'

const BrazeBanner = dynamic(() => import('@/components/BrazeBanner'), { ssr: false })
// Decagon-only, client-only singleton: the in-chat payment drawer opened by the
// agent's open_cart event (see src/lib/decagon.ts). Loaded lazily so Braintree
// never enters the bundle for the Zendesk arm.
const DecagonPaymentDrawer = dynamic(() => import('@/components/DecagonPaymentDrawer'), {
  ssr: false,
})
// Decagon-only, client-only singleton: the in-chat sign-in popover opened by the agent's
// open_login event (see src/lib/decagon.ts).
const DecagonLoginPopover = dynamic(() => import('@/components/DecagonLoginPopover'), {
  ssr: false,
})
const BrazeBannerCarousel = dynamic(() => import('@/components/BrazeBannerCarousel'), {
  ssr: false,
})
const DatadogInit = dynamic(() => import('@/components/DatadogInit'), {
  ssr: false,
  loading: () => null,
})
const LoginModal = dynamic(() => import('lib/components/LoginModal/LoginModal'), {
  ssr: false,
  loading: () => null,
}) as LoginModalComponent
const SignUpModal = dynamic(() => import('lib/components/SignUpModal/SignUpModal'), {
  ssr: false,
  loading: () => null,
}) as SignUpModalComponent
const CompleteRegistrationModal = dynamic(
  () => import('lib/components/CompleteRegistrationModal/CompleteRegistrationModal'),
  {
    ssr: false,
    loading: () => null,
  },
) as CompleteRegistrationModalComponent
const HelpCenter = dynamic(() => import('lib/components/HelpCenter/HelpCenter'), {
  ssr: false,
  loading: () => (
    <div className="sr-only" aria-live="polite">
      Loading help center...
    </div>
  ),
}) as HelpCenterComponent
const MobileVerifyModal = dynamic(
  () => import('lib/components/MobileVerification/MobileVerifyModal'),
  {
    ssr: false,
    loading: () => null,
  },
) as MobileVerifyModalComponent
const AddUserModal = dynamic(() => import('lib/components/AddUserModal/AddUserModal'), {
  ssr: false,
  loading: () => null,
}) as AddUserModalComponent
const ProRedirectModal = dynamic(() => import('./ProRedirectModal'), {
  ssr: false,
  loading: () => null,
}) as ProRedirectModalComponent

type IdleRequestCallback = (deadline: { didTimeout: boolean; timeRemaining: () => number }) => void
type IdleRequestOptions = { timeout?: number }
type WindowWithIdleCallback = Window & {
  requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number
  cancelIdleCallback?: (handle: number) => void
}

const ClientPortal: React.FC<ClientPortalProps> = ({ queryClient, persisterRef }) => {
  const queryClientFromContext = useQueryClient()
  const { enabled: isSmsMarketingOn } = useSmsMarketingEnabled()
  const pathname = usePathname()

  // Prevent hydration mismatch by deferring user-based UI until after mount
  const mounted = useMounted()
  const pageLoaded = usePageLoad()
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setPortalRoot(document.getElementById('client-portal-root'))
  }, [])

  const { track, loaded: rudderstackLoaded } = useRudderstackAnalytics()
  const {
    isInitialized: isBrazeInitialized,
    logCustomEvent,
    setUserEmail,
    setUserCustomAttribute,
    aliasRudderstackUser,
  } = useBraze()
  const { mutate: createWelcomeLead } = useCreateWelcomeLead()

  const LEAD_CAPTURE_COOKIE = 'braze_lead_capture_shown'
  useEffect(() => {
    if (!isBrazeInitialized) return
    // No promotional Braze email modal or SMS-only popup on the order pages.
    // Adding pathname to the deps also cancels a pending return-visit timer when
    // the user navigates INTO a suppressed page (cleanup runs, no reschedule).
    if (isPromoModalSuppressedPath(pathname)) return

    const hasCookie = document.cookie
      .split(';')
      .some((c) => c.trim().startsWith(`${LEAD_CAPTURE_COOKIE}=`))
    if (hasCookie) {
      // The Braze lead-capture modal already ran on a prior visit (cookie set),
      // so Braze won't fire it again. Pop the SMS opt-in in its place instead.
      // Hold briefly so it doesn't pop the instant Braze initializes — see
      // RETURN_VISIT_QUIET_MS. openSmsOnlyModal self-dedupes via
      // SMS_ONLY_MODAL_SHOWN_COOKIE, so this surfaces at most once per browser
      // and no-ops when SMS-only is disabled.
      const timeoutId = window.setTimeout(() => openSmsOnlyModal(), RETURN_VISIT_QUIET_MS)
      return () => window.clearTimeout(timeoutId)
    }

    logCustomEvent('braze_show_lead_capture')
  }, [isBrazeInitialized, logCustomEvent, pathname])

  // Stitch this visitor's device-mode Braze profile to the cloud-mode profile
  // RudderStack keys on `rudder_id:<anonymousId>`. Gated on both SDKs being
  // ready so getRudderstackAnonymousId() resolves, and runs at page load — well
  // before the lead modal can be submitted — so the backend's cloud-mode
  // `generate_lead` lands on this same profile. See useBraze#aliasRudderstackUser.
  useEffect(() => {
    if (!isBrazeInitialized || !rudderstackLoaded) return
    aliasRudderstackUser()
  }, [isBrazeInitialized, rudderstackLoaded, aliasRudderstackUser])

  // Receives form data from the Braze lead-capture custom-HTML in-app message.
  // The modal posts back to the parent on submit with:
  //   window.parent.postMessage(
  //     { type: 'braze_lead_capture_submitted', email, coupon },
  //     '*',
  //   )
  // We then write email + coupon to the Braze user profile, drop the dedupe
  // cookie, and log a follow-up custom event for downstream segmentation.
  useEffect(() => {
    if (!isBrazeInitialized) return

    const handleMessage = (event: MessageEvent): void => {
      const data = event.data as { type?: string; email?: unknown; coupon?: unknown } | undefined
      if (!data || data.type !== 'braze_lead_capture_submitted') return

      const email = typeof data.email === 'string' ? data.email.trim() : ''
      // const coupon = typeof data.coupon === 'string' ? data.coupon.trim() : ''

      if (email) setUserEmail(email)
      // if (coupon) setUserCustomAttribute('lead_coupon', coupon)

      // 1 year — modal should not re-prompt the same browser session.
      const oneYear = 60 * 60 * 24 * 365
      document.cookie = `${LEAD_CAPTURE_COOKIE}=1; max-age=${oneYear}; path=/; samesite=lax`

      if (email) {
        // When SMS marketing is on, the SMS opt-in replaces Braze's message, so
        // tear it down here (twin of useBraze.ts fireLeadCapture). When it's OFF
        // there's no opt-in to replace it, so leave Braze's message up and let
        // it show its own confirmation on signup. Read the flag live (getter,
        // not the hook value) so this handler never uses a stale closure.
        if (isSmsMarketingEnabled()) {
          closeActiveBrazeInAppMessage()
        }

        // Fire-and-forget: the lead POST is slow in production, so the SMS
        // opt-in must not wait on it (see useBraze.ts fireLeadCapture).
        createWelcomeLead({ email })

        // TEMP timing instrumentation (strip once verified): measure submit ->
        // SMS modal shown on the postMessage path.
        const submittedAt = performance.now()
        // eslint-disable-next-line no-console
        console.log('[sms-timing] braze email submitted (postMessage path)')

        // Open the SMS opt-in on a fixed short beat after submit, independent of
        // the lead POST. openSmsOnlyModal self-guards on the SMS-marketing brand
        // flag and the sms_only_modal_shown dedupe cookie.
        window.setTimeout(() => {
          // eslint-disable-next-line no-console
          console.log(
            `[sms-timing] opening SMS-only modal ${Math.round(
              performance.now() - submittedAt,
            )}ms after submit (postMessage path)`,
          )
          openSmsOnlyModal(email)
        }, POST_CAPTURE_QUIET_MS)

        // GA4 recommended lead event (matches the Ship&Play homepage lead
        // capture in content/LeadCapture.tsx). Deduped against the iframe
        // submit listener in useBraze.ts, which fires for the same submission.
        trackGenerateLead(email, 'Braze Lead Capture Modal')
      }

      logCustomEvent('braze_lead_capture_submitted', {
        ...(email ? { email } : {}),
        // ...(coupon ? { coupon } : {}),
      })
      track?.('braze_lead_capture_submitted', {
        ...(email ? { email } : {}),
        // ...(coupon ? { coupon } : {}),
      })
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [
    createWelcomeLead,
    isBrazeInitialized,
    logCustomEvent,
    setUserCustomAttribute,
    setUserEmail,
    track,
  ])

  const setPendingSignUpData = usePendingUserStore((state) => state.setPendingData)
  const authCode = usePendingUserStore((state) => state.authCode)
  const setAppleAuthCode = usePendingUserStore((state) => state.setAuthCode)
  const pendingSignUpData = usePendingUserStore(
    useShallow((state) => ({
      firstName: state.firstName,
      lastName: state.lastName,
      email: state.email,
    })),
  )

  const [openDialog, hideDialog] = useModal((state) => [state.openDialog, state.hideDialog])

  const [phoneNumber, setPhoneNumber] = useState('')
  const [createPartialUserErrors, setCreatePartialUserErrors] = useState<MutationError[]>([])

  const [searchTerm, setSearchTerm] = useState<string>('')
  const [searchEmail, setSearchEmail] = useState<string>('')

  const { mutate: travelersUpsertMutation } = useTravelersUpsertApi()
  const travelers = useCartStore((store) => store.travelers) as Traveler[]

  const cartOrder = useCartStore((state) => state.order)
  const cartStoreHasHydrated = useCartStore((state) => state.hasHydrated)
  const sessionStoreHasHydrated = useSessionStore((store) => store.hasHydrated)

  const incompleteUserModalVisible = useIncompleteUserStore((store) => store.modalVisible)
  const setIncompleteUserModalVisible = useIncompleteUserStore((store) => store.setModalVisible)

  const incompleteUser = useIncompleteUserStore(
    useShallow((state) => ({
      firstName: state.firstName,
      lastName: state.lastName,
      email: state.email,
      countryCode: state.countryCode,
      phone: state.phone,
    })),
  )

  const setIncompleteUser = useIncompleteUserStore((store) => store.setUserDetails)

  const onTokenExchangeSuccess =
    (source: 'facebook' | 'google') =>
    (response: TokenExchangeResponse): void => {
      travelersUpsertMutation(travelers)
      hideDialog('login-modal')
      setErrors([])

      track?.('login', { location: `${source.toUpperCase()} Modal`, email: response.user_email })

      if (!response.complete) {
        setIncompleteUser({
          firstName: response.user_first_name || '',
          lastName: response.user_last_name || '',
          email: response.user_email || '',
          countryCode: response.user_country_code?.toLowerCase() || '',
          phone: response.user_phone_number || '',
        })

        openDialog(COMPLETE_REGISTRATION_MODAL_ID)
      } else {
        if (response.auth_token && response.user_email) {
          setAuth(response.auth_token, response.user_id, response.user_email)
          window.location.reload()
        } else {
          addError(new Error('Authentication failed: Incomplete response from server.'))
        }
      }
    }

  const onAppleTokenExchangeSuccess = (response: AppleTokenExchangeResponse): void => {
    hideDialog('login-modal')

    if (response.existing || response.registered) {
      travelersUpsertMutation(travelers)
      setAuthSuccessful(true)
      setErrors([])

      setAuth(response.auth_token!, response.user_id!, response.email!, response.roles)
      window.location.reload()
    } else {
      setPendingSignUpData({
        firstName: response.firstName || '',
        lastName: response.lastName || '',
        email: response.email || '',
      })

      if (response.code) setAppleAuthCode(response.code)

      openDialog('signup-modal')
    }
  }

  const { initiateAppleAuth, appleTokenExchangeError } = useAppleAuth(onAppleTokenExchangeSuccess)

  const { initiateFacebookAuth, facebookTokenExchangeError } = useFacebookAuth(
    onTokenExchangeSuccess('facebook'),
  )

  const { initiateGoogleAuth, googleTokenExchangeError } = useGoogleAuth(
    onTokenExchangeSuccess('google'),
  )

  const [errors, setErrors] = React.useState<string[]>([])
  const [authSuccessful, setAuthSuccessful] = useState<boolean>(false)
  const [proUserType, setProUserType] = useState<'pro' | null>(null)
  const { mutate: createUser, error: createUserError } = useCreateUser()
  const { mutate: recordSmsOptIn } = useRecordSmsOptInApi()
  const { mutate: basicAuthLogin, error: basicAuthError } = useSessionsApi()
  const { mutate: createPartialUser, isPending: createPartialUserPending } =
    usePartialUserCreateApi()
  const { data: searchedUser } = useSearchUsersApi(searchEmail)
  const { data: autoCompleteItems } = useAutoCompleteProFacilitiesApi(searchTerm)
  const userId = useSessionStore((store) => store.userId)
  const impersonatingUser = useCartStore((store) => store.impersonatingUser)
  const setImpersonatingUser = useCartStore((store) => store.setImpersonatingUser)
  const appendShipmentNote = useCartStore((store) => store.appendShipmentNote)
  const addUserModalShown = useCartStore((store) => store.addUserModalShown)
  const setAddUserModalShown = useCartStore((store) => store.setAddUserModalShown)
  const cartToken = useCartStore((state) => state.cartToken)
  const cartDisplayId = useCartStore((state) => state.cart?.displayId)

  const authToken = useSessionStore((store) => store.authToken)
  const setAuth = useSessionStore((store) => store.setAuth)
  const userEmail = useSessionStore((store) => store.userEmail)
  const userRoles = useSessionStore((store) => store.userRoles)
  const clearSession = useSessionStore((store) => store.clearSession)
  const hideMobileMenuDialog = useMobileMenuModal((state) => state.hideDialog)
  const mobileMenuDialogOpen = useMobileMenuModal((state) => state.dialogOpen)

  const microSite = useMicroSiteStore((state) => state.microSite)

  const onPasswordResetComplete = useCallback(
    (details: PasswordResetSuccessDetails): void => {
      setAuth(
        details.authToken,
        details.userId,
        details.email,
        details.roles,
        details.mobileVerified,
        details.termsAccepted,
      )
      setAuthSuccessful(true)
      setErrors([])
      track?.('login', { location: 'Login Modal - Password Reset', email: details.email })
      hideDialog('login-modal')
      travelersUpsertMutation(travelers, {
        onSettled: () => window.location.reload(),
      })
    },
    [hideDialog, setAuth, track, travelersUpsertMutation, travelers],
  )

  const onOtpLoginComplete = useCallback(
    (details: OtpLoginSuccessDetails): void => {
      setAuth(
        details.authToken,
        details.userId,
        details.email,
        details.roles,
        details.mobileVerified,
        details.termsAccepted,
      )
      setAuthSuccessful(true)
      setErrors([])
      track?.('login', { location: 'Login Modal - OTP', email: details.email })
      hideDialog('login-modal')
      travelersUpsertMutation(travelers, {
        onSuccess: () => window.location.reload(),
      })
    },
    [hideDialog, setAuth, track, travelersUpsertMutation, travelers],
  )

  const { mutateAsync: logOut, isPending: logOutIsPending } = useLogOutSessionApi()

  const currentStep = routes.find(({ path }) => path === pathname)?.stepNumber || 0

  const guestAutoLoggedOutRef = useRef(false)

  useEffect(() => {
    const isGuestUser = userRoles.includes('guest_user')

    if (!mounted) return
    if (!sessionStoreHasHydrated || !cartStoreHasHydrated) return
    if (!authToken) return
    if (!isGuestUser) return
    // Only clear guest session/cart on step 1.
    if (currentStep !== 1) return
    if (!cartOrder) return
    if (guestAutoLoggedOutRef.current) return

    guestAutoLoggedOutRef.current = true

    void (async () => {
      try {
        persisterRef.current?.removeClient()
        await logOut({ authToken })
      } catch {
        // best-effort: still clear client state
      } finally {
        queryClient.clear()
        clearSession()
        setImpersonatingUser(undefined)
        window.location.reload()
      }
    })()
  }, [
    authToken,
    cartOrder,
    cartStoreHasHydrated,
    clearSession,
    currentStep,
    persisterRef,
    logOut,
    mounted,
    queryClient,
    sessionStoreHasHydrated,
    setImpersonatingUser,
    userRoles,
  ])

  // Always call hook but only use data after mount to match SSR
  const userQuery = useUserApi(!!authToken)
  const activeShipments = mounted ? userQuery.data?.activeShipments ?? 0 : 0
  const firstName = mounted ? userQuery.data?.firstName : undefined
  const signedIn = mounted && !!authToken
  const isPro = mounted && checkIsPro(userRoles)
  const isAdmin = mounted && checkIsAdmin(userRoles)
  const isAdminPlus = mounted && checkIsAdminPlus(userRoles)
  const isGuestUser = mounted && !!userRoles?.some((role) => role === 'guest_user')
  const gtmId = isPro ? GTM_IDS[1] : GTM_IDS[0]
  const ddUser =
    mounted && userQuery.data
      ? {
          id: userQuery.data.id,
          name: `${userQuery.data.firstName} ${userQuery.data.lastName}`,
          email: userEmail ?? undefined,
          isUser: true,
          isPro,
          isAdmin: hasAdminRole(userRoles),
          isTravelAgent: userRoles?.includes('agent'),
          isCampManager: userRoles?.includes('camp_manager'),
          isVip: userRoles?.includes('vip'),
        }
      : undefined

  const userLinks =
    signedIn && !isGuestUser
      ? [
          ...brand.layoutContent.signedInUserLinks(activeShipments),
          {
            name: 'Log Out',
            disabled: logOutIsPending,
            onClick: async (e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault()
              persisterRef.current?.removeClient()

              if (authToken) await logOut({ authToken })

              queryClient.clear()
              clearSession()
              setImpersonatingUser(undefined)
              window.location.reload()
            },
          },
        ]
      : [
          {
            name: 'Sign In',
            onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault()
              if (mobileMenuDialogOpen) hideMobileMenuDialog()
              // Open the login modal
              openDialog('login-modal')
            },
          },
        ]
  const accountText =
    !mounted || isGuestUser ? 'Sign In' : signedIn && firstName ? `Hi, ${firstName}` : 'Sign In'
  const shouldShowAccountDropdown = signedIn && !isGuestUser

  const clearGuestSession = useCallback(() => {
    if (!isGuestUser) return

    clearSession()
    void queryClientFromContext.invalidateQueries({ queryKey: ['currentUser'] })
  }, [clearSession, isGuestUser, queryClientFromContext])

  useAuthGate()
  useEffect(() => {
    setOptimizelyUser({
      isPro,
      isAdmin,
      isAdminPlus,
      userId,
    })
  }, [userId, isPro, isAdmin, isAdminPlus])

  // Check for pro users and trigger redirect
  useEffect(() => {
    if (mounted && userRoles && userRoles.length > 0) {
      const redirectTriggered = handleAdminProRedirect(userRoles, openDialog)
      if (redirectTriggered) {
        // Set user type for modal display (only pro users trigger redirect)
        setProUserType('pro')
      }
    }
  }, [mounted, userRoles, openDialog])

  useEffect(() => {
    if (userQuery.data && !userQuery.data.complete && !incompleteUserModalVisible) {
      setIncompleteUser({
        firstName: userQuery.data?.firstName || '',
        lastName: userQuery.data?.lastName || '',
        email: userQuery.data?.email || '',
        phone: userQuery.data?.phoneNumber || '',
        countryCode: userQuery.data?.countryCode || '',
      })
      setIncompleteUserModalVisible(true)
      openDialog(COMPLETE_REGISTRATION_MODAL_ID)
    }
  }, [
    incompleteUserModalVisible,
    openDialog,
    setIncompleteUser,
    setIncompleteUserModalVisible,
    userQuery.data,
  ])

  // Sync phone number from user query so MobileVerifyModal has it for
  // signed-up users who haven't gone through the registration modal flow.
  useEffect(() => {
    if (userQuery.data?.phoneNumber && !phoneNumber) {
      setPhoneNumber(userQuery.data.phoneNumber)
    }
  }, [userQuery.data?.phoneNumber, phoneNumber])

  const onLoginFormSubmit = (data: Record<string, string>): void => {
    const { email: login_id, password } = data

    if (pathname.startsWith('/book/'))
      track?.('booking_flow_auth_sign_in_clicked', {
        location: 'Login Modal',
        email_prefilled: !!userEmail,
      })

    basicAuthLogin(
      { login_id, password, cart_token: cartToken },
      {
        onSuccess: (response) => {
          // basic auth success comes through here
          travelersUpsertMutation(travelers)
          setAuth(
            response.auth_token,
            response.id,
            response.email,
            response.roles,
            response.mobile_verified,
            response.terms,
          )
          setAuthSuccessful(true)
          setErrors([])
          track?.('login', { location: 'Login Modal', email: response.email })
          window.location.reload()
        },
      },
    )
  }

  const buildSignUpUserPayload = useCallback(
    (formData: SignUpFormData): UserCreatePayload => {
      const userPayload: UserCreatePayload = {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone_number: formData.phone,
        country_code: formData.country,
        sms_tracking_optin: formData.smsTrackingOptin,
        // This might blow up the user model record creation.
        // interested_in_partnering: formData.interestedInPartnering,
        requested_club: formData.companyName,
        hear_about_us: formData.howDidYouHearAboutUs,
        other_hear_about_us: formData.otherHearAboutUs,
        terms: formData.agreeToTOS,
        mobile_verified: formData.mobileVerified,
        brand_id: brand.key,
      }

      const { password, confirmPassword } = formData
      if (password) userPayload.password = password
      if (confirmPassword) userPayload.password_confirmation = confirmPassword

      if (authCode) {
        userPayload.auth_data = { provider: 'apple_id', identity_token: authCode }
      }

      return userPayload
    },
    [authCode],
  )

  // Marketing SMS opt-in from the sign-up form (ACCOUNT_SIGNUP touchpoint,
  // marketing group only — transactional stays on sms_tracking_optin /
  // updateMobileVerification). Fired once the account exists so the backend
  // can stitch identity; fire-and-forget, because marketing opt-in must never
  // block account creation or verification. The backend records the
  // subscription as SUBSCRIBED on submit; double opt-in confirmation
  // (GSE-12174) is tracked on the record separately, never as a state.
  const recordSignUpSmsOptIn = useCallback(
    (formData: SignUpFormData): void => {
      const variables = buildSignUpSmsOptInVariables(formData)
      if (!variables) return

      recordSmsOptIn(variables, {
        onSuccess: () => {
          // Client-side GA4 event; the backend fires its own server-side
          // RudderStack generate_lead (channel: sms) — same split as the
          // other SMS opt-in surfaces.
          sendGTMEvent({
            event: 'generate_lead',
            method: 'Sign Up SMS',
            ...(formData.email ? { user_email: formData.email } : {}),
          })
        },
      })
    },
    [recordSmsOptIn],
  )

  // Called from SignUpModal when the create-password form is valid, BEFORE
  // transitioning to mobile verification. Creates the user up-front so the
  // mobile-verify step operates on an authenticated user; the modal stays
  // open and the page does not reload here — finalisation happens in
  // `onSignUpModalSubmit` after the verify step.
  const onSignUpModalCreateUserBeforeVerify = useCallback(
    (formData: SignUpFormData): Promise<{ success: boolean; error?: string }> => {
      if (pathname.startsWith('/book/'))
        track?.('booking_flow_auth_sign_up_clicked', {
          location: 'Sign Up Modal',
          email_prefilled: !!userEmail,
        })

      const userPayload = buildSignUpUserPayload(formData)

      return new Promise((resolve) => {
        createUser(userPayload, {
          onSuccess: (data) => {
            const { auth_token, id: user_id, email, terms, roles } = data || {}
            // The backend returns a neutral 201 with no session when the email
            // already belongs to an account (it will not hand back a session
            // for an unverified email). Don't set a broken auth state or
            // advance to verify — steer the user to log in.
            if (!auth_token || !user_id) {
              resolve({
                success: false,
                error: 'An account already exists with this email address. Please log in instead.',
              })
              return
            }
            sendGTMEvent({ event: 'sign_up', terms, user_id })
            setAuth(auth_token, user_id, email, roles, undefined, terms)
            recordSignUpSmsOptIn(formData)
            resolve({ success: true })
          },
          onError: (error) => {
            resolve({ success: false, error: error?.message })
          },
        })
      })
    },
    [buildSignUpUserPayload, createUser, pathname, recordSignUpSmsOptIn, setAuth, track, userEmail],
  )

  const onSignUpModalSubmit = (formData: SignUpFormData): void => {
    // If the early-create flow already ran, the user is already authed and
    // the verifyCode mutation has marked them mobile-verified server-side.
    // Just finalise the modal.
    if (useSessionStore.getState().authToken) {
      travelersUpsertMutation(travelers)
      hideDialog('signup-modal')
      setErrors([])
      window.location.reload()
      return
    }

    // Fallback path (no early create — e.g. modal embedded somewhere that
    // doesn't pass `onCreateUserBeforeVerify`): create the user now.
    const userPayload = buildSignUpUserPayload(formData)

    if (pathname.startsWith('/book/'))
      track?.('booking_flow_auth_sign_up_clicked', {
        location: 'Sign Up Modal',
        email_prefilled: !!userEmail,
      })

    createUser(userPayload, {
      onSuccess: ({ auth_token, id: user_id, email, terms, roles }) => {
        sendGTMEvent({ event: 'sign_up', terms, user_id })
        travelersUpsertMutation(travelers)
        setAuth(auth_token, user_id, email, roles, undefined, terms)
        // Best effort on this vestigial path — the reload below may cut the
        // request short; the primary flow fires in CreateUserBeforeVerify.
        recordSignUpSmsOptIn(formData)
        hideDialog('signup-modal')
        setErrors([])
        window.location.reload()
      },
    })
  }

  const onCompleteRegistrationModalSubmit = async (
    data: Record<string, unknown>,
  ): Promise<void> => {
    await userQuery.refetch()

    travelersUpsertMutation(travelers)

    hideDialog(COMPLETE_REGISTRATION_MODAL_ID)

    setPhoneNumber((data?.user as { phoneNumber?: string })?.phoneNumber ?? '')

    openDialog(MOBILE_VERIFY_MODAL_ID)
  }

  /**
   * addError:
   * Adds an error message to the local `errors` state if it is not already present.
   * This consolidates errors from various parts of the authentication process.
   *
   * @param error - The error object received from authentication hooks or APIs.
   */
  const addError = (error: Error | null): void => {
    if (error?.message) {
      setErrors((prev) => (prev[0] === error.message ? prev : [error.message, ...prev]))
    }
  }

  // Use effect hooks to capture errors from each 3rd party authentication method
  useEffect(() => addError(googleTokenExchangeError), [googleTokenExchangeError])
  useEffect(() => addError(facebookTokenExchangeError), [facebookTokenExchangeError])
  useEffect(() => addError(appleTokenExchangeError), [appleTokenExchangeError])
  useEffect(() => addError(basicAuthError), [basicAuthError])

  const { headerType, showSiteHeaderButton } = useHeader({ pathname })
  const shouldDeferSupportUi = pathname === '/booking-widget' || pathname === '/'
  const [supportUiReady, setSupportUiReady] = useState(!shouldDeferSupportUi)

  useEffect(() => {
    if (!shouldDeferSupportUi) {
      setSupportUiReady(true)
      return
    }
    if (!pageLoaded) return

    let cancelled = false
    const enableUi = (): void => {
      if (!cancelled) setSupportUiReady(true)
    }

    const idleWindow = window as WindowWithIdleCallback
    const idleHandle = idleWindow.requestIdleCallback?.(enableUi, { timeout: 2500 })
    const timeoutId = window.setTimeout(enableUi, 2000)

    return () => {
      cancelled = true
      if (idleHandle && idleWindow.cancelIdleCallback) {
        idleWindow.cancelIdleCallback(idleHandle)
      }
      window.clearTimeout(timeoutId)
    }
  }, [pageLoaded, shouldDeferSupportUi])

  // Decagon and Zendesk are mutually exclusive. The arm comes from the
  // Optimizely-set chat_provider cookie (A/B), else the env-slug fallback.
  const chatProvider = useResolvedChatProvider()
  useZendesk({ enabled: pageLoaded && chatProvider === 'zendesk' })
  useDecagon({ enabled: pageLoaded && chatProvider === 'decagon' })

  useEffect(() => {
    if (
      signedIn &&
      userId &&
      userEmail &&
      (userRoles.includes('sales_manager') || userRoles.includes('csr')) &&
      currentStep === 1
    ) {
      if (!impersonatingUser) {
        setImpersonatingUser({ id: userId, email: userEmail })
      }

      if (!addUserModalShown) {
        openDialog('add-user-modal')
      }
    }
  }, [
    openDialog,
    signedIn,
    userId,
    userEmail,
    userRoles,
    setImpersonatingUser,
    currentStep,
    addUserModalShown,
    impersonatingUser,
  ])

  const { defaultHelpCenterItems, packageAndProtectionHelpCenterItems, shipPageHelpCenterItems } =
    brand.layoutContent

  const [helpCenterItems, setHelpCenterItems] = useState<HelpCenterItem[]>(defaultHelpCenterItems)

  useEffect(() => {
    switch (pathname) {
      case '/book/ship':
        setHelpCenterItems([...shipPageHelpCenterItems, ...defaultHelpCenterItems])
        break
      case '/book/pay':
        setHelpCenterItems([...packageAndProtectionHelpCenterItems, ...defaultHelpCenterItems])
        break
      default:
        setHelpCenterItems(defaultHelpCenterItems)
    }
  }, [
    defaultHelpCenterItems,
    packageAndProtectionHelpCenterItems,
    pathname,
    shipPageHelpCenterItems,
  ])

  const onUserAdd = ({ userID, email, name, phoneNumber }: AddUserModalOnAddProps): void => {
    if (userID && email) {
      setImpersonatingUser({ id: userID, email, name, phoneNumber })
    }
  }

  const onNoteAdd = (note: string, type: ShipmentNoteType): void => {
    appendShipmentNote({ note, type })
    hideDialog('note-modal')
  }

  const onPartialUserSubmit: SubmitHandler<FormInput> = (formData: FormInput): void => {
    createPartialUser(
      {
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        email: formData.email || '',
        phoneNumber: formData.phone || '',
        countryCode: formData.country || '',
        hearAboutUs: formData.howDidYouHearAboutUs || '',
        otherHearAboutUs: formData.otherHearAboutUs || '',
      },
      {
        onSuccess(data) {
          if (data.success) {
            onUserAdd({
              userID: data.user.id,
              email: data.user.email,
              name: data.user.name,
              phoneNumber: data.user.phoneNumber,
            })

            setAddUserModalShown(true)
            hideDialog('add-user-modal')
          } else {
            setCreatePartialUserErrors(data.errors)
          }
        },
      },
    )
  }

  const siteHeaderProps: SiteHeaderProps = {
    type: headerType as SiteHeaderProps['type'],
    logoLink: '/',
    brand: brand.key,
    brands: SITE_HEADER_BRANDS,
    accountText,
    signedIn: shouldShowAccountDropdown,
    hideAccountIcon:
      pathname === '/track-orders' ||
      pathname === '/order-confirmation' ||
      pathname.startsWith('/order-tracking')
        ? isGuestUser
        : false,
    userLinks,
    links: SITE_HEADER_LINKS,
    helpLinks: SITE_HEADER_HELP_LINKS,
    // Pass a React node so callers can provide a custom client-side button component
    button: showSiteHeaderButton ? (
      <Button
        onClick={() => {
          track?.('Checkout Started')
          // Hard navigation (honors booking-widget-target) — a router.push
          // soft-nav into the statically-exported /book/ship can render the
          // raw RSC payload (GSE-11866).
          redirectFromBookingWidget('')
        }}
        className="!min-h-12 w-full type-body-9"
      >
        Get Started
      </Button>
    ) : undefined,
    impersonatingUser,
    microSite: microSite || undefined,
    onNoteAdd,
  }

  // useModal only works within a component hook—inject onClick handlers
  // for 'Login' and 'Sign Up' links in the 'Account' footer section
  const siteFooterMetaWithOnClicks: SiteFooterProps = {
    ...brand.layoutContent.siteFooterMeta,
    primaryLinks: brand.layoutContent.siteFooterMeta.primaryLinks.map((section) => {
      if (section.title !== 'Account') return section

      const loginItem = {
        title: 'Login',
        url: '#',
        onClick: (e: React.MouseEvent) => {
          e.preventDefault()
          openDialog('login-modal')
        },
      }

      const signupItem = {
        title: 'Sign Up',
        url: '#',
        onClick: (e: React.MouseEvent) => {
          e.preventDefault()
          openDialog('signup-modal')
        },
      }

      const myAccountItem = {
        title: 'My Account',
        url: '/users/edit',
      }

      const children = signedIn
        ? [myAccountItem, ...section.children]
        : [loginItem, signupItem, ...section.children]

      return { ...section, children }
    }),
  }

  const clickAppleLogin = useCallback((): void => {
    clearGuestSession()

    if (pathname.startsWith('/book/'))
      track?.('booking_flow_auth_sign_in_clicked', {
        location: 'Login Modal',
        email_prefilled: !!userEmail,
      })

    initiateAppleAuth()
  }, [clearGuestSession, initiateAppleAuth, pathname, track, userEmail])

  const clickGoogleLogin = useCallback((): void => {
    clearGuestSession()

    if (pathname.startsWith('/book/'))
      track?.('booking_flow_auth_sign_in_clicked', {
        location: 'Login Modal',
        email_prefilled: !!userEmail,
      })

    initiateGoogleAuth()
  }, [clearGuestSession, initiateGoogleAuth, pathname, track, userEmail])

  const clickFacebookLogin = useCallback((): void => {
    clearGuestSession()

    if (pathname.startsWith('/book/'))
      track?.('booking_flow_auth_sign_in_clicked', {
        location: 'Login Modal',
        email_prefilled: !!userEmail,
      })

    initiateFacebookAuth()
  }, [clearGuestSession, initiateFacebookAuth, pathname, track, userEmail])

  const displayLoginModal = useCallback((): void => {
    if (pathname.startsWith('/book/'))
      track?.('booking_flow_auth_prompt_shown', {
        email_detected: !!userEmail,
        logged_in_status: !!authToken,
        trigger_source: 'Login Modal',
      })
  }, [authToken, pathname, track, userEmail])

  // Existing single-placement banner (kept during carousel rollout transition)
  const bannerPlacementId =
    BRAZE_BANNER_PLACEMENTS[pathname] ??
    (!excludeBannerOn(pathname) ? `non-book-pages-${brand.shortName}` : undefined)

  // Carousel placement IDs for this page (memoised to keep a stable reference
  // so BrazeBannerCarousel's setup effect doesn't re-run on every render)
  const combinedCarouselIds = useMemo(
    () => [...getCarouselPlacementIds(pathname), ...getAllPagesCarouselIds(pathname)],
    [pathname],
  )

  // Use carousel when available (prod), fall back to single banner (non-prod / transition)
  const bannerNode =
    combinedCarouselIds.length > 0 ? (
      <div className="relative z-20 w-full">
        <BrazeBannerCarousel
          placementIds={combinedCarouselIds}
          className="w-full"
          onBannerLoad={() => {}}
        />
      </div>
    ) : bannerPlacementId ? (
      <div className="relative z-20 w-full">
        <BrazeBanner placementId={bannerPlacementId} className="w-full" onBannerLoad={() => {}} />
      </div>
    ) : null
  const banner = portalRoot && bannerNode ? createPortal(bannerNode, portalRoot) : null
  // Shipplay opts into the new SiteLayout chrome per-page (SiteNav + SiteUserBar +
  // ConciergeTeam + SiteFooter mounted from src/components/shipplay/SiteLayout).
  // ClientPortal must NOT double-mount the legacy SiteHeader / SiteFooter for that
  // brand, else both chromes stack (see GSE-11839).
  const useShipplayChrome = brand.key === 'shipplay'
  const shouldRenderSiteHeader = !useShipplayChrome && !STATIC_HEADER_PATHS.has(pathname)

  // Initialize or swap GTM when gtmId changes
  useDynamicGtm(gtmId)

  return (
    <>
      {/* Client-only enhancements: modals, analytics, auth-aware portal; hydrates after HTML. */}
      {(!shouldDeferSupportUi || supportUiReady) && <DatadogInit user={ddUser} />}
      {banner}
      {chatProvider === 'decagon' && <DecagonPaymentDrawer />}
      {chatProvider === 'decagon' && <DecagonLoginPopover />}
      <AirbrakeErrorBoundary userId={userId} cartId={cartDisplayId}>
        {shouldRenderSiteHeader && <SiteHeader {...siteHeaderProps} />}
        {!useShipplayChrome && <SiteFooter {...siteFooterMetaWithOnClicks} />}
        {supportUiReady && (
          <>
            <LoginModal
              brand={brand.key}
              errorMessage={errors[0]}
              onPasswordResetComplete={onPasswordResetComplete}
              onOtpLoginComplete={onOtpLoginComplete}
              cartToken={cartToken}
              login={() => {}}
              onDisplay={displayLoginModal}
              onSubmit={onLoginFormSubmit}
              googleLogin={clickGoogleLogin}
              facebookLogin={clickFacebookLogin}
              appleLogin={clickAppleLogin}
              authSuccessful={authSuccessful}
            />
            <SignUpModal
              brand={brand.key}
              initialData={pendingSignUpData}
              errorMessage={createUserError?.message}
              onSubmit={onSignUpModalSubmit}
              onCreateUserBeforeVerify={onSignUpModalCreateUserBeforeVerify}
              smsMarketingEnabled={isSmsMarketingOn}
            />
            {incompleteUserModalVisible && (
              <CompleteRegistrationModal
                brand={brand.key}
                defaultValues={incompleteUser}
                onSubmit={onCompleteRegistrationModalSubmit}
              />
            )}
            <HelpCenter items={helpCenterItems} />

            <MobileVerifyModal brand={brand.key} phoneNumber={phoneNumber} />

            <AddUserModal
              id="add-user-modal"
              email={searchEmail}
              searchedUser={searchedUser}
              setEmail={setSearchEmail}
              setSearchTerm={setSearchTerm}
              autoCompleteItems={autoCompleteItems}
              onAdd={onUserAdd}
              onAfterClose={() => setAddUserModalShown(true)}
              onPartialUserSubmit={onPartialUserSubmit}
              createPartialUserPending={createPartialUserPending}
              createPartialUserErrors={createPartialUserErrors}
            />

            {proUserType && <ProRedirectModal userType={proUserType} />}

            {/* Standalone SMS-only opt-in, queued after a successful Braze email
                Welcome-modal submit (see useBraze.ts / the postMessage handler
                above). Distinct dialog id so it never collides with the footer
                SmsOptInModal. Flag-gated so it ships zero DOM until rollout. */}
            {isSmsMarketingOn && (
              <SmsOptInModal
                id={SMS_ONLY_MODAL_ID}
                brand={brand.key}
                source="SMS_ONLY_MODAL"
                gtmMethod="SMS Only Modal"
              />
            )}
          </>
        )}
      </AirbrakeErrorBoundary>
    </>
  )
}

export default ClientPortal
